import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/lib/i18n";
import { onLevel } from "@/lib/orbLevel";

/* HUD orb. Rings rotate via a single rAF integrator (angle += speed·dt) so state changes never reset angles —
   only the speed eases. Level (real mic/audio RMS) drives halo+core; when audio stops it settles in ~400ms.
   The `layoutId` lets the same orb morph hero⇄corner (position/size/halo together). */
const ACCENT = {
  idle: "#38BDF8", wake_listening: "#22D3EE", listening: "#22D3EE",
  thinking: "#60A5FA", speaking: "#38BDF8", executing: "#FBBF24", error: "#F87171",
  mic_off: "#F87171", disconnected: "#64748B",
};
const SPEED = { idle: 1, wake_listening: 1.1, listening: 1.3, thinking: 2.2, speaking: 1.25, executing: 1.6, error: 0.4, mic_off: 0, disconnected: 0 };
const BASE = { a: 4, b: -9, c: 16, d: -10.5 }; // deg/s at speed 1

const arc = (r, startDeg, sweepDeg) => {
  const s = (startDeg * Math.PI) / 180, e = ((startDeg + sweepDeg) * Math.PI) / 180;
  return `M ${100 + r * Math.cos(s)} ${100 + r * Math.sin(s)} A ${r} ${r} 0 ${sweepDeg > 180 ? 1 : 0} 1 ${100 + r * Math.cos(e)} ${100 + r * Math.sin(e)}`;
};
const reduced = () => typeof window !== "undefined" &&
  (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || document.documentElement.dataset.reduceMotion === "1");

export const JarvisOrb = ({ state = "idle", size = 260, compact = false, layoutId = "jarvis-orb",
  onActivate, active = false }) => {
  const { t } = useI18n();
  const ref = useRef(null);
  const rings = useRef({});
  const anim = useRef({ angles: { a: 0, b: 0, c: 0, d: 0 }, speed: 1, target: 1, lvl: 0, lvlTarget: 0 });
  const accent = ACCENT[state] || ACCENT.idle;
  const dim = state === "mic_off" || state === "disconnected";
  const reactive = state === "listening" || state === "speaking";

  useEffect(() => { anim.current.target = SPEED[state] ?? 1; }, [state]);
  useEffect(() => {
    if (!reactive) { anim.current.lvlTarget = 0; return; }
    return onLevel((v) => { anim.current.lvlTarget = v; });
  }, [reactive]);

  useEffect(() => {
    let raf, last = performance.now();
    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000); last = now;
      const a = anim.current;
      a.speed += (a.target - a.speed) * Math.min(1, dt * 3);           // speed eases, never jumps
      a.lvl += (a.lvlTarget - a.lvl) * Math.min(1, dt * (a.lvlTarget > a.lvl ? 14 : 5)); // fast attack, ~400ms release
      if (!reduced()) {
        for (const k of Object.keys(BASE)) {
          a.angles[k] = (a.angles[k] + BASE[k] * a.speed * dt) % 360;
          const el = rings.current[k]; if (el) el.setAttribute("transform", `rotate(${a.angles[k].toFixed(2)} 100 100)`);
        }
      }
      ref.current?.style.setProperty("--lvl", a.lvl.toFixed(3));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const label = (t(`state_${state}`) || state).toUpperCase();
  // The orb itself is the single voice control when onActivate is given: a real <button>, so Tab/Enter/Space
  // work natively. Shape, size and animations stay identical — only hover/focus feedback is added.
  const Tag = onActivate ? motion.button : motion.div;
  const interactive = onActivate
    ? { type: "button", onClick: onActivate, "aria-label": active ? t("endConv") : t("startConv"),
        "aria-pressed": active, "data-active": active ? "1" : "0", title: active ? t("endConv") : t("startConv") }
    : {};
  return (
    <Tag layoutId={layoutId} ref={ref} transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }} {...interactive}
      className={`orb ${compact ? "orb-compact" : ""} ${onActivate ? "orb-interactive" : ""} flex flex-col items-center justify-center select-none`}
      data-testid={compact ? "mini-orb-visual" : "jarvis-orb"} data-state={state}
      style={{ "--accent": accent, "--lvl": 0, width: compact ? size : `clamp(220px, min(${size}px, 82vw, 56vh), ${size}px)` }}>
      <div className="orb-stage" style={{ width: compact ? size : `clamp(220px, min(${size}px, 82vw, 56vh), ${size}px)`, height: compact ? size : `clamp(220px, min(${size}px, 82vw, 56vh), ${size}px)` }}>
        <div className="orb-halo" data-reactive={reactive ? "1" : "0"} />
        <svg viewBox="0 0 200 200" className="orb-svg" aria-hidden="true">
          {/* speaking sound-wave rings (opacity follows real level) */}
          {[104, 112, 120].map((r, i) => (
            <circle key={r} cx="100" cy="100" r={r} fill="none" stroke="var(--accent)" strokeWidth="0.5" className={`orb-wave orb-wave-${i}`} />
          ))}
          {/* cardinal dots */}
          {[0, 90, 180, 270].map((d) => <circle key={d} cx="100" cy="4" r="1.3" fill="var(--accent)" opacity={dim ? 0.3 : 0.9} transform={`rotate(${d} 100 100)`} />)}
          {/* outer tick ring (rotates slowly) */}
          <g ref={(el) => (rings.current.a = el)} opacity={dim ? 0.2 : state === "listening" ? 0.9 : 0.5} className="orb-ticks">
            {Array.from({ length: compact ? 36 : 90 }).map((_, i) => (
              <line key={i} x1="100" y1="9" x2="100" y2={i % 5 === 0 ? "13" : "11"} stroke={i % 5 === 0 ? "var(--accent)" : "#CBD5E1"} strokeWidth="0.5" transform={`rotate(${i * (compact ? 10 : 4)} 100 100)`} />
            ))}
          </g>
          {/* thin dashed white ring */}
          <circle ref={(el) => (rings.current.b = el)} cx="100" cy="100" r="82" fill="none" stroke="#E2E8F0" strokeWidth="0.5" strokeDasharray="1.5 4" opacity={dim ? 0.15 : 0.4} />
          {/* bright cyan segmented arcs */}
          <g ref={(el) => (rings.current.c = el)} fill="none" stroke="var(--accent)" strokeWidth={state === "thinking" || state === "speaking" ? 2.6 : 2.1} strokeLinecap="round" opacity={dim ? 0.3 : 0.95} className="orb-accent">
            <path d={arc(72, -30, state === "thinking" ? 150 : 95)} /><path d={arc(72, 150, state === "thinking" ? 110 : 70)} /><path d={arc(72, 262, 36)} />
          </g>
          {/* secondary thin cyan arcs, reverse */}
          <g ref={(el) => (rings.current.d = el)} fill="none" stroke="var(--accent)" strokeWidth="0.9" strokeLinecap="round" opacity={dim ? 0.2 : 0.65}>
            <path d={arc(64, 20, 120)} /><path d={arc(64, 190, 60)} /><path d={arc(64, 280, 30)} />
          </g>
          {/* inner thin white circle + dark core */}
          <circle className="orb-core" cx="100" cy="100" r="54" fill="url(#coreGrad)" />
          <circle cx="100" cy="100" r="56" fill="none" stroke="#F8FAFC" strokeWidth="0.8" opacity={dim ? 0.3 : 0.85} />
          <defs>
            <radialGradient id="coreGrad" cx="50%" cy="45%" r="70%">
              <stop offset="0%" stopColor="#0A1326" /><stop offset="75%" stopColor="#060B16" /><stop offset="100%" stopColor="#03060C" />
            </radialGradient>
          </defs>
          <text x="100" y={compact ? 107 : 105} textAnchor="middle" fontFamily="Outfit, system-ui, sans-serif" fontWeight="400"
            fontSize={compact ? 24 : 15} letterSpacing={compact ? 2 : 5.5} fill={dim ? "#94A3B8" : "#F8FAFC"} className="orb-title">{compact ? "J" : "JARVIS"}</text>
          {/* orbiting light particles (thin bright sparks on the accent ring) */}
          {!compact && (
            <g className="orb-particles" opacity={dim ? 0 : 1}>
              {[0, 70, 140, 215, 300].map((d, i) => (
                <circle key={d} cx="100" cy="28" r={i % 2 ? 0.8 : 1.3} fill="#BAE6FD" transform={`rotate(${d} 100 100)`} className={`orb-spark orb-spark-${i}`} />
              ))}
            </g>
          )}
        </svg>
      </div>
      {/* No visible helper text: the orb animation alone communicates the state. The label stays for
          screen readers / tests only. */}
      {!compact && (
        <span className="sr-only" data-testid="orb-state-label">{label}</span>
      )}
    </Tag>
  );
};

export default JarvisOrb;
